import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Wrench } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { gmao, machineCatalog as machineCatalogApi } from "@/api";
import MachineFormModal from "@/components/gmao/MachineFormModal";
import ActaModal from "@/components/ui/ActaModal";
import { formatApiError } from "@/lib/api";


const STATUS_CLASSES = {
  active: "border-[var(--do-success)] bg-[var(--do-success-soft)] text-[var(--do-ink)]",
  maintenance: "border-[var(--do-warning)] bg-[var(--do-warning-soft)] text-[var(--do-ink)]",
  inactive: "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-ink)]",
  retired: "border-[var(--do-danger)] bg-[var(--do-danger-soft)] text-[var(--do-ink)]",
};

function formatDate(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR").format(parsed);
}

function installationLabel(installation) {
  if (!installation?.date) return "—";
  return installation.kind === "installed"
    ? `Installée le ${formatDate(installation.date)}`
    : `Prévue le ${formatDate(installation.date)}`;
}

function addressLabel(row) {
  return [
    row.address,
    [row.postalCode, row.city].filter(Boolean).join(" "),
  ].filter(Boolean).join(" · ") || "—";
}

function Badge({ children, tone = "neutral" }) {
  const className = tone === "competitor"
    ? "border-[var(--do-warning)] bg-[var(--do-warning-soft)] text-[var(--do-ink)]"
    : tone === "pending"
      ? "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-ink)]"
      : STATUS_CLASSES[tone] || "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-ink)]";

  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-bold ${className}`}>
      {children}
    </span>
  );
}

function DeclarationModal({ open, prospect, catalogItems, saving, onClose, onSubmit }) {
  const [form, setForm] = useState({
    origin: "competitor",
    machine_catalog_id: "",
    machine_name: "",
    manufacturer: "",
    serial_number: "",
    commissioning_date: "",
    address: "",
    city: "",
    postal_code: "",
    country: "FR",
    notes: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      origin: "competitor",
      machine_catalog_id: "",
      machine_name: "",
      manufacturer: "",
      serial_number: "",
      commissioning_date: "",
      address: prospect?.address || "",
      city: prospect?.city || "",
      postal_code: prospect?.postalCode || prospect?.postal_code || "",
      country: ["FR", "BE"].includes(prospect?.country) ? prospect.country : "FR",
      notes: "",
    });
  }, [open, prospect]);

  if (!open) return null;

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const internal = form.origin === "internal";

  return (
    <ActaModal
      open={open}
      onClose={onClose}
      eyebrow="Qualité des données"
      title="Déclarer une machine"
      subtitle="La déclaration sera contrôlée avant d’entrer dans le parc officiel."
      size="lg"
      closeDisabled={saving}
      testId="contact-machine-declaration-modal"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({
            ...form,
            prospect_id: prospect.id,
            commissioning_date: form.commissioning_date || null,
          });
        }}
      >

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-xs font-bold text-[var(--do-muted)]">Origine *</span>
            <select value={form.origin} onChange={(event) => update("origin", event.target.value)} className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3" data-testid="machine-declaration-origin">
              <option value="internal">Notre entreprise</option>
              <option value="competitor">Concurrente</option>
            </select>
          </label>

          {internal ? (
            <label className="space-y-1.5">
              <span className="text-xs font-bold text-[var(--do-muted)]">Équipement du catalogue *</span>
              <select required value={form.machine_catalog_id} onChange={(event) => update("machine_catalog_id", event.target.value)} className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3">
                <option value="">Sélectionner</option>
                {catalogItems.map((item) => (
                  <option key={item.id} value={item.id}>{item.name || item.reference || item.id}</option>
                ))}
              </select>
            </label>
          ) : (
            <label className="space-y-1.5">
              <span className="text-xs font-bold text-[var(--do-muted)]">Machine *</span>
              <input required value={form.machine_name} onChange={(event) => update("machine_name", event.target.value)} className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3" />
            </label>
          )}

          {!internal && (
            <label className="space-y-1.5">
              <span className="text-xs font-bold text-[var(--do-muted)]">Fabricant</span>
              <input value={form.manufacturer} onChange={(event) => update("manufacturer", event.target.value)} className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3" />
            </label>
          )}

          <label className="space-y-1.5">
            <span className="text-xs font-bold text-[var(--do-muted)]">N° de série{internal ? " *" : ""}</span>
            <input required={internal} value={form.serial_number} onChange={(event) => update("serial_number", event.target.value)} className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3" />
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-bold text-[var(--do-muted)]">Date d’installation connue</span>
            <input type="date" value={form.commissioning_date} onChange={(event) => update("commissioning_date", event.target.value)} className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3" />
          </label>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <label className="space-y-1.5 sm:col-span-3">
            <span className="text-xs font-bold text-[var(--do-muted)]">Adresse</span>
            <input value={form.address} onChange={(event) => update("address", event.target.value)} className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3" />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-bold text-[var(--do-muted)]">Code postal</span>
            <input value={form.postal_code} onChange={(event) => update("postal_code", event.target.value)} className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3" />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-bold text-[var(--do-muted)]">Ville</span>
            <input value={form.city} onChange={(event) => update("city", event.target.value)} className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3" />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-bold text-[var(--do-muted)]">Pays</span>
            <select value={form.country} onChange={(event) => update("country", event.target.value)} className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3">
              <option value="FR">France</option>
              <option value="BE">Belgique</option>
            </select>
          </label>
        </div>

        <label className="mt-5 block space-y-1.5">
          <span className="text-xs font-bold text-[var(--do-muted)]">Notes</span>
          <textarea rows={3} value={form.notes} onChange={(event) => update("notes", event.target.value)} className="w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2" />
        </label>

        <div className="mt-6 flex justify-end gap-2 border-t border-[var(--do-border)] pt-4">
          <button type="button" disabled={saving} onClick={onClose} className="rounded-xl border border-[var(--do-border)] px-4 py-2.5 text-sm font-bold">Annuler</button>
          <button type="submit" disabled={saving} className="rounded-xl bg-[var(--do-accent)] px-4 py-2.5 text-sm font-bold text-[var(--do-on-accent)]" data-testid="machine-declaration-submit">
            {saving ? "Enregistrement…" : "Envoyer pour validation"}
          </button>
        </div>
      </form>
    </ActaModal>
  );
}


export default function ContactMachinesPanel({ prospect, prospectId, user }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [declarationOpen, setDeclarationOpen] = useState(false);
  const [machineOpen, setMachineOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogError, setCatalogError] = useState("");

  const role = user?.actualRole || user?.role;
  const canDeclare = ["admin", "manager", "maintenance_manager", "user"].includes(role);
  const canValidate = ["admin", "maintenance_manager"].includes(role);
  const canCreateDirectly = role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await gmao.getClientMachineWorkspace(prospectId);
      setRows(Array.isArray(payload?.items) ? payload.items : []);
    } catch (requestError) {
      setRows([]);
      setError(formatApiError(requestError.response?.data?.detail) || "Impossible de charger les machines du client.");
    } finally {
      setLoading(false);
    }
  }, [prospectId]);

  const loadCatalog = useCallback(async () => {
    try {
      const items = await machineCatalogApi.list();
      const declarableTypes = new Set(["machine", "accessory", "handpiece"]);
      setCatalogItems(
        Array.isArray(items)
          ? items.filter(
              (item) =>
                item.active !== false &&
                declarableTypes.has(item.type || "machine")
            )
          : []
      );
      setCatalogError("");
    } catch {
      setCatalogItems([]);
      setCatalogError("catalog_load_failed");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (declarationOpen || machineOpen) loadCatalog();
  }, [declarationOpen, machineOpen, loadCatalog]);

  const officialMachines = useMemo(
    () =>
      rows.filter(
        (row) =>
          row.kind === "machine" &&
          (row.equipmentType || "machine") === "machine"
      ),
    [rows]
  );

  const officialCount = officialMachines.length;

  const parentMachineById = useMemo(
    () =>
      Object.fromEntries(
        officialMachines
          .filter((row) => row.machineId)
          .map((row) => [row.machineId, row])
      ),
    [officialMachines]
  );

  const displayRows = useMemo(() => {
    const officialEquipmentRows = rows.filter(
      (row) => row.kind === "machine"
    );
    const handpieces = officialEquipmentRows.filter(
      (row) => row.equipmentType === "handpiece"
    );
    const handpiecesByParent = new Map();

    handpieces.forEach((row) => {
      const parentId = row.parentMachineId || "";
      if (!handpiecesByParent.has(parentId)) {
        handpiecesByParent.set(parentId, []);
      }
      handpiecesByParent.get(parentId).push(row);
    });

    const ordered = [];

    officialMachines.forEach((row) => {
      ordered.push(row);
      ordered.push(
        ...(handpiecesByParent.get(row.machineId) || [])
      );
    });

    ordered.push(
      ...(handpiecesByParent.get("") || [])
    );

    officialEquipmentRows
      .filter(
        (row) =>
          (row.equipmentType || "machine") !== "machine" &&
          row.equipmentType !== "handpiece"
      )
      .forEach((row) => ordered.push(row));

    rows
      .filter((row) => row.kind !== "machine")
      .forEach((row) => ordered.push(row));

    return ordered;
  }, [rows, officialMachines]);

  const submitDeclaration = async (payload) => {
    setSaving(true);
    try {
      await gmao.createMachineDeclaration(payload);
      toast.success(payload.origin === "internal" ? "Machine déclarée et envoyée pour validation" : "Machine concurrente enregistrée");
      setDeclarationOpen(false);
      await load();
    } catch (requestError) {
      toast.error(formatApiError(requestError.response?.data?.detail) || "Déclaration impossible");
    } finally {
      setSaving(false);
    }
  };

  const validateDeclaration = async (row) => {
    try {
      await gmao.validateMachineDeclaration(row.id);
      toast.success(row.origin === "competitor" ? "Machine concurrente validée" : "Machine ajoutée au parc");
      await load();
    } catch (requestError) {
      toast.error(formatApiError(requestError.response?.data?.detail) || "Validation impossible");
    }
  };

  const createDirectMachine = async (payload) => {
    setSaving(true);
    try {
      await gmao.createMachine({
        ...payload,
        prospect_id: prospectId,
        location_status: "installed",
      });
      toast.success("Équipement ajouté au client");
      setMachineOpen(false);
      await load();
    } catch (requestError) {
      toast.error(formatApiError(requestError.response?.data?.detail) || "Création impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="au-card overflow-hidden" data-testid="contact-machines-panel">
      <div className="flex flex-col gap-3 border-b border-[var(--do-border)] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div>
          <div className="label-eyebrow">Machines</div>
          <h2 className="mt-1 font-heading text-lg font-black text-[var(--do-ink)]">Parc du client</h2>
          <p className="mt-1 text-xs text-[var(--do-muted)]">{officialCount} machine{officialCount > 1 ? "s" : ""} dans le parc officiel.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canDeclare && (
            <button type="button" onClick={() => setDeclarationOpen(true)} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm font-bold" data-testid="contact-machine-declare">
              <Plus size={17} weight="bold" /> Déclarer une machine
            </button>
          )}
          {canCreateDirectly && (
            <button type="button" onClick={() => setMachineOpen(true)} className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-[var(--do-accent)] px-3 text-sm font-bold text-[var(--do-on-accent)]" data-testid="contact-machine-add-admin">
              <Wrench size={17} weight="bold" /> Ajouter une machine
            </button>
          )}
        </div>
      </div>

      {error && <div className="p-5 text-sm font-semibold text-[var(--do-danger)]">{error}</div>}
      {loading ? (
        <div className="p-8 text-center text-sm text-[var(--do-muted)]">Chargement des machines…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm font-semibold text-[var(--do-muted)]">Aucune machine enregistrée.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left text-sm">
            <thead className="bg-[var(--do-bg-2)] text-xs uppercase tracking-wide text-[var(--do-muted)]">
              <tr>
                <th className="px-4 py-3">Machine</th>
                <th className="px-4 py-3">N° série</th>
                <th className="px-4 py-3">État</th>
                <th className="px-4 py-3">Installation / livraison</th>
                <th className="px-4 py-3">Adresse</th>
                <th className="px-4 py-3">Garantie</th>
                <th className="px-4 py-3">Maintenance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--do-border)]">
              {displayRows.map((row) => {
                const clickable = Boolean(row.machineId);
                return (
                  <tr key={`${row.kind}-${row.id}`} className={clickable ? "cursor-pointer hover:bg-[var(--do-bg-2)]" : ""} onClick={() => clickable && navigate(`/app/gmao/v2/parc/${encodeURIComponent(row.machineId)}`)} data-testid="contact-machine-row">
                    <td className={`px-4 py-3 ${row.equipmentType === "handpiece" && row.parentMachineId ? "pl-8" : ""}`}>
                      <div className="font-black text-[var(--do-ink)]">{row.name}</div>
                      {row.equipmentType === "handpiece" && (
                        <div className="mt-0.5 text-xs font-semibold text-[var(--do-muted)]">
                          {row.parentMachineId
                            ? `Pièce à main · ${parentMachineById[row.parentMachineId]?.name || "machine rattachée"}`
                            : "Pièce à main indépendante"}
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {row.origin === "competitor" && <Badge tone="competitor">Concurrente</Badge>}
                        {row.qualityStatus === "pending_validation" && <Badge tone="pending">À valider</Badge>}
                        {canValidate && row.origin === "internal" && row.qualityStatus === "pending_validation" && (
                          <button type="button" onClick={(event) => { event.stopPropagation(); validateDeclaration(row); }} className="text-xs font-black text-[var(--do-accent)] underline">Valider</button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono-stat">{row.serialNumber || "—"}</td>
                    <td className="px-4 py-3">{row.status ? <Badge tone={row.status}>{row.statusLabel}</Badge> : "—"}</td>
                    <td className="px-4 py-3 font-bold">{installationLabel(row.installation)}</td>
                    <td className="max-w-[260px] px-4 py-3 text-[var(--do-muted)]">{addressLabel(row)}</td>
                    <td className="px-4 py-3">{formatDate(row.warrantyEndDate)}</td>
                    <td className="px-4 py-3">{formatDate(row.nextMaintenanceAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DeclarationModal open={declarationOpen} prospect={prospect} catalogItems={catalogItems} saving={saving} onClose={() => setDeclarationOpen(false)} onSubmit={submitDeclaration} />

      <MachineFormModal
        open={machineOpen}
        prospects={prospect ? [prospect] : []}
        machines={officialMachines.map((row) => ({
          id: row.machineId,
          name: row.name,
          serialNumber: row.serialNumber,
          equipmentType: "machine",
          machineCatalogId: row.machineCatalogId,
          prospectId,
        }))}
        catalogItems={catalogItems}
        catalogLoadError={catalogError}
        onReloadCatalog={loadCatalog}
        saving={saving}
        onClose={() => setMachineOpen(false)}
        onSubmit={createDirectMachine}
      />
    </section>
  );
}
